import { X, Palette, Box, Grid, Type, Layers } from "lucide-react";
import { Button } from "@/ui/components/primitives/button";
import { Card } from "@/ui/components/primitives/card";
import { Badge } from "@/ui/components/primitives/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/ui/components/primitives/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/ui/components/primitives/tabs";
import { useAuth } from "@/context/AuthContext";

export function DesignSystemInfo() {
  const { user } = useAuth();
  if ((user?.role || user?.role?.toUpperCase?.()) !== "ADMIN") return null;
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="fixed bottom-24 right-8 z-50 shadow-lg md:bottom-8">
          <Palette className="h-4 w-4 mr-2" strokeWidth={1.5} />
          Design System
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
        <DialogHeader>
          <DialogTitle>FinPay Design System</DialogTitle>
          <DialogDescription>
            Production-ready design system following atomic design principles
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="colors" className="mt-6">
          <TabsList className="grid grid-cols-5 w-full">
            <TabsTrigger value="colors">Colors</TabsTrigger>
            <TabsTrigger value="spacing">Spacing</TabsTrigger>
            <TabsTrigger value="typography">Typography</TabsTrigger>
            <TabsTrigger value="components">Components</TabsTrigger>
            <TabsTrigger value="tokens">Tokens</TabsTrigger>
          </TabsList>

          {/* Colors Tab */}
          <TabsContent value="colors" className="space-y-6">
            <div>
              <h3 className="mb-4">Semantic Color System</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <Card className="p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="h-10 w-10 rounded-lg bg-primary" />
                    <div>
                      <p className="font-medium">Primary</p>
                      <code className="text-xs text-muted-foreground">#a855f7</code>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Main brand color, CTAs, links
                  </p>
                </Card>

                <Card className="p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="h-10 w-10 rounded-lg bg-success" />
                    <div>
                      <p className="font-medium">Success</p>
                      <code className="text-xs text-muted-foreground">#22c55e</code>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Success states, payin transactions
                  </p>
                </Card>

                <Card className="p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="h-10 w-10 rounded-lg bg-destructive" />
                    <div>
                      <p className="font-medium">Destructive</p>
                      <code className="text-xs text-muted-foreground">#ef4444</code>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Error states, payout transactions
                  </p>
                </Card>

                <Card className="p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="h-10 w-10 rounded-lg bg-warning" />
                    <div>
                      <p className="font-medium">Warning</p>
                      <code className="text-xs text-muted-foreground">#f59e0b</code>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Warning states, pending status
                  </p>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Spacing Tab */}
          <TabsContent value="spacing" className="space-y-6">
            <div>
              <h3 className="mb-4">8px Grid System</h3>
              <p className="text-sm text-muted-foreground mb-6">
                All spacing follows strict 8px grid for perfect alignment
              </p>
              <div className="space-y-3">
                {[
                  { value: "4px", class: "p-1", use: "Tight spacing, icons" },
                  { value: "8px", class: "p-2", use: "Small gaps, badges" },
                  { value: "12px", class: "p-3", use: "Button padding" },
                  { value: "16px", class: "p-4", use: "Card padding" },
                  { value: "24px", class: "p-6", use: "Section spacing" },
                  { value: "32px", class: "p-8", use: "Page padding" },
                  { value: "48px", class: "p-12", use: "Large gaps" },
                  { value: "64px", class: "p-16", use: "Extra large gaps" },
                ].map((spacing) => (
                  <div key={spacing.value} className="flex items-center gap-4">
                    <code className="w-16 text-sm font-medium">{spacing.value}</code>
                    <div className={`${spacing.class} bg-primary/20 rounded`}>
                      <div className="h-4 bg-primary rounded" />
                    </div>
                    <span className="text-sm text-muted-foreground">{spacing.use}</span>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Typography Tab */}
          <TabsContent value="typography" className="space-y-6">
            <div>
              <h3 className="mb-4">Typography Scale</h3>
              <div className="space-y-6">
                <div>
                  <h1>Heading 1 - Page Titles</h1>
                  <code className="text-xs text-muted-foreground">text-2xl, font-medium</code>
                </div>
                <div>
                  <h2>Heading 2 - Section Titles</h2>
                  <code className="text-xs text-muted-foreground">text-xl, font-medium</code>
                </div>
                <div>
                  <h3>Heading 3 - Card Titles</h3>
                  <code className="text-xs text-muted-foreground">text-lg, font-medium</code>
                </div>
                <div>
                  <h4>Heading 4 - Subsections</h4>
                  <code className="text-xs text-muted-foreground">text-base, font-medium</code>
                </div>
                <div>
                  <p>Body Text - Regular content</p>
                  <code className="text-xs text-muted-foreground">text-base, font-normal</code>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Small Text - Captions, hints</p>
                  <code className="text-xs text-muted-foreground">text-sm, muted-foreground</code>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Components Tab */}
          <TabsContent value="components" className="space-y-6">
            <div>
              <h3 className="mb-4">Atomic Components</h3>
              <div className="space-y-6">
                <div>
                  <h4 className="mb-3">Atoms</h4>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm">Button</Button>
                    <Badge>Badge</Badge>
                    <Badge variant="secondary">Secondary</Badge>
                    <Badge variant="outline">Outline</Badge>
                  </div>
                </div>

                <div>
                  <h4 className="mb-3">Molecules</h4>
                  <div className="grid gap-3">
                    <Card className="p-4">
                      <p className="text-sm">Card Component</p>
                    </Card>
                  </div>
                </div>

                <div>
                  <h4 className="mb-3">Organisms</h4>
                  <p className="text-sm text-muted-foreground">
                    Complex components: MetricCard, StatusBadge, TransactionTable
                  </p>
                </div>

                <div>
                  <h4 className="mb-3">Templates</h4>
                  <p className="text-sm text-muted-foreground">
                    Page layouts: Dashboard, Transactions, Settings
                  </p>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Tokens Tab */}
          <TabsContent value="tokens" className="space-y-6">
            <div>
              <h3 className="mb-4">Design Tokens</h3>
              <div className="space-y-4">
                <Card className="p-4">
                  <h4 className="mb-2">Border Radius</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <code>--radius-sm</code>
                      <span className="text-muted-foreground">8px (0.5rem)</span>
                    </div>
                    <div className="flex justify-between">
                      <code>--radius-md</code>
                      <span className="text-muted-foreground">12px (0.75rem)</span>
                    </div>
                  </div>
                </Card>

                <Card className="p-4">
                  <h4 className="mb-2">Shadows</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <code>shadow-sm</code>
                      <span className="text-muted-foreground">Subtle elevation</span>
                    </div>
                    <div className="flex justify-between">
                      <code>shadow-md</code>
                      <span className="text-muted-foreground">Card elevation</span>
                    </div>
                    <div className="flex justify-between">
                      <code>shadow-lg</code>
                      <span className="text-muted-foreground">Dialog elevation</span>
                    </div>
                  </div>
                </Card>

                <Card className="p-4">
                  <h4 className="mb-2">Motion</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <code>duration-150</code>
                      <span className="text-muted-foreground">Fast transitions</span>
                    </div>
                    <div className="flex justify-between">
                      <code>duration-200</code>
                      <span className="text-muted-foreground">Standard transitions</span>
                    </div>
                    <div className="flex justify-between">
                      <code>ease-out</code>
                      <span className="text-muted-foreground">Easing function</span>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <div className="mt-6 p-4 bg-muted rounded-lg">
          <p className="text-sm text-muted-foreground">
            <strong>Design Philosophy:</strong> This system ensures infinite scalability
            through consistent tokens, reusable components, and strict adherence to the
            8px grid. Every element is optimized for both light and dark modes with
            perfect accessibility.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
